import { useState, useEffect } from 'react';
import { sparesAPI } from '../../api';
import toast from 'react-hot-toast';
import styles from './Spares.module.css';

const STATUS_FILTERS = [
  { key:'all',      label:'All Parts' },
  { key:'low',      label:'Low Stock' },
  { key:'in_stock', label:'In Stock'  },
];

function AddPartModal({ onClose, onSave }) {
  const [form, setForm] = useState({ part_number:'', name:'', category:'', unit:'Nos', stock_qty:0, reorder_level:0, unit_cost:0, location:'', supplier:'' });
  const [saving, setSaving] = useState(false);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const submit = async () => {
    if (!form.part_number || !form.name) { toast.error('Part number and name required'); return; }
    setSaving(true);
    try { await sparesAPI.create(form); toast.success('Part added'); onSave(); }
    catch(e) { toast.error(e.response?.data?.detail||'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className={styles.modal_overlay} onClick={e => e.target === e.currentTarget}>
      <div className={styles.modal} onClick={e=>e.stopPropagation()}>
        <div className={styles.modal_header}><h2>Add Spare Part</h2><button onClick={onClose} className={styles.modal_close}>✕</button></div>
        <div className={styles.modal_body}>
          <div className={styles.form_grid}>
            <div className={styles.form_group}><label>Part Number *</label><input value={form.part_number} onChange={e=>set('part_number',e.target.value)} placeholder="e.g. BRG-6205-2RS"/></div>
            <div className={styles.form_group}><label>Part Name *</label><input value={form.name} onChange={e=>set('name',e.target.value)} placeholder="e.g. Deep Groove Ball Bearing"/></div>
            <div className={styles.form_group}><label>Category</label><input value={form.category} onChange={e=>set('category',e.target.value)} placeholder="e.g. Bearings"/></div>
            <div className={styles.form_group}><label>Unit</label><select value={form.unit} onChange={e=>set('unit',e.target.value)}><option>Nos</option><option>Kg</option><option>Ltr</option><option>Mtr</option><option>Set</option><option>Box</option></select></div>
            <div className={styles.form_group}><label>Stock Qty</label><input type="number" min="0" value={form.stock_qty} onChange={e=>set('stock_qty',e.target.value)}/></div>
            <div className={styles.form_group}><label>Reorder Level</label><input type="number" min="0" value={form.reorder_level} onChange={e=>set('reorder_level',e.target.value)}/></div>
            <div className={styles.form_group}><label>Unit Cost (₹)</label><input type="number" min="0" value={form.unit_cost} onChange={e=>set('unit_cost',e.target.value)}/></div>
            <div className={styles.form_group}><label>Location</label><input value={form.location} onChange={e=>set('location',e.target.value)} placeholder="Store A, Shelf 3"/></div>
            <div className={`${styles.form_group} ${styles.form_full}`}><label>Supplier</label><input value={form.supplier} onChange={e=>set('supplier',e.target.value)} placeholder="Supplier name"/></div>
          </div>
        </div>
        <div className={styles.modal_footer}>
          <button className={styles.btn_cancel} onClick={onClose}>Cancel</button>
          <button className={styles.btn_submit} onClick={submit} disabled={saving}>{saving?'Saving…':'Add Part'}</button>
        </div>
      </div>
    </div>
  );
}

function EditStockModal({ part, onClose, onSave }) {
  const [qty, setQty] = useState(part.stock_qty);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try { await sparesAPI.update(part.id, { stock_qty: Number(qty) }); toast.success('Stock updated'); onSave(); }
    catch(e) { toast.error('Failed'); } finally { setSaving(false); }
  };
  return (
    <div className={styles.modal_overlay} onClick={e => e.target === e.currentTarget}>
      <div className={`${styles.modal} ${styles.modal_sm}`} onClick={e=>e.stopPropagation()}>
        <div className={styles.modal_header}><h2>Update Stock</h2><button onClick={onClose} className={styles.modal_close}>✕</button></div>
        <div className={styles.modal_body}>
          <div style={{background:'#f0f4ff',borderRadius:8,padding:'10px 14px',marginBottom:16,fontSize:13,color:'#1e2a3b'}}>
            <strong>{part.name}</strong> ({part.part_number})<br/>
            <span style={{fontSize:12,color:'#64748b'}}>Current stock: {part.stock_qty} {part.unit} · Reorder at: {part.reorder_level}</span>
          </div>
          <div className={styles.form_group}><label>New Stock Qty ({part.unit})</label><input type="number" min="0" value={qty} onChange={e=>setQty(e.target.value)} autoFocus/></div>
        </div>
        <div className={styles.modal_footer}>
          <button className={styles.btn_cancel} onClick={onClose}>Cancel</button>
          <button className={styles.btn_submit} onClick={submit} disabled={saving}>{saving?'Saving…':'Update Stock'}</button>
        </div>
      </div>
    </div>
  );
}

function DetailPanel({ part, onEditStock }) {
  if (!part) return (
    <div className={styles.detail_empty}>
      <div style={{fontSize:36}}>📦</div>
      <div>Select a part to view details</div>
    </div>
  );
  const isLow = part.stock_qty <= part.reorder_level;
  const totalVal = (part.stock_qty * part.unit_cost).toLocaleString('en-IN', {minimumFractionDigits:2});
  return (
    <div className={styles.detail_panel}>
      <div className={styles.dp_header}>
        <span style={{fontFamily:'monospace',fontSize:12,fontWeight:700,color:'#64748b'}}>{part.part_number}</span>
        <span className={styles.badge} style={{background:isLow?'#fee2e2':'#dcfce7',color:isLow?'#b91c1c':'#15803d'}}>{isLow?'Low Stock':'In Stock'}</span>
      </div>
      <div className={styles.dp_title}>{part.name}</div>
      {isLow && <div className={styles.dp_warn}>⚠ Stock below reorder level</div>}

      <div className={styles.dp_actions}>
        <button className={styles.dp_btn_primary} onClick={onEditStock}>📥 Update Stock</button>
      </div>

      {/* Stock gauge */}
      <div style={{margin:'14px 0',padding:'12px',background:'#f8f9fc',borderRadius:8}}>
        <div style={{display:'flex',justifyContent:'space-between',fontSize:12,color:'#64748b',marginBottom:6}}>
          <span>Stock Level</span><span style={{fontWeight:700,color:isLow?'#ef4444':'#1e2a3b'}}>{part.stock_qty} / {part.reorder_level} {part.unit}</span>
        </div>
        <div style={{height:8,background:'#e2e8f0',borderRadius:4,overflow:'hidden'}}>
          <div style={{height:'100%',borderRadius:4,background:isLow?'#ef4444':'#22c55e',width:`${Math.min(100,(part.stock_qty/Math.max(part.reorder_level*2,1))*100)}%`,transition:'width .3s'}}/>
        </div>
        <div style={{fontSize:12,color:'#64748b',marginTop:6}}>Total value: ₹{totalVal}</div>
      </div>

      <div className={styles.dp_divider}/>
      <div className={styles.dp_section}>Part Details</div>
      {[
        ['Category', part.category],
        ['Unit', part.unit],
        ['Unit Cost', `₹${Number(part.unit_cost).toLocaleString('en-IN')}`],
        ['Location', part.location],
        ['Supplier', part.supplier],
      ].filter(([,v])=>v).map(([l,v])=>(
        <div key={l} className={styles.dp_row}><span>{l}</span><strong>{v}</strong></div>
      ))}
    </div>
  );
}

export default function SparesPage() {
  const [spares,   setSpares]  = useState([]);
  const [loading,  setLoading] = useState(true);
  const [selected, setSelected]= useState(null);
  const [showAdd,  setShowAdd] = useState(false);
  const [showEdit, setShowEdit]= useState(false);
  const [search,   setSearch]  = useState('');
  const [stockFilter, setStockFilter] = useState('all');

  const load = () => { setLoading(true); sparesAPI.list({q:search}).then(r=>setSpares(r.data.data)).finally(()=>setLoading(false)); };
  useEffect(()=>{ load(); },[search]);

  const filtered = spares.filter(p => {
    if (stockFilter === 'low')      return p.stock_qty <= p.reorder_level;
    if (stockFilter === 'in_stock') return p.stock_qty > p.reorder_level;
    return true;
  });

  const lowCount = spares.filter(p => p.stock_qty <= p.reorder_level).length;
  const cats = [...new Set(spares.map(p=>p.category).filter(Boolean))];

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <button className={styles.new_btn} onClick={()=>setShowAdd(true)}>＋ Add Part</button>
        <div className={styles.search_wrap}>
          <span className={styles.search_icon}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></span>
          <input className={styles.search_input} placeholder="Search by name or part number…" value={search} onChange={e=>setSearch(e.target.value)}/>
          {search && <button className={styles.search_clear} onClick={()=>setSearch('')}>✕</button>}
        </div>
        <div className={styles.toolbar_right}>
          {lowCount > 0 && <span className={styles.badge} style={{background:'#fee2e2',color:'#b91c1c',padding:'4px 10px',borderRadius:20,fontSize:12,fontWeight:600}}>⚠ {lowCount} low stock</span>}
          <span className={styles.total_badge}>{spares.length} parts</span>
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.filter_col}>
          {STATUS_FILTERS.map(f => {
            const cnt = f.key==='all'?spares.length : f.key==='low'?lowCount : spares.length-lowCount;
            return (
              <div key={f.key} className={`${styles.filter_item} ${stockFilter===f.key?styles.filter_active:''}`} onClick={()=>setStockFilter(f.key)}>
                <span className={styles.filter_dot} style={{background:f.key==='low'?'#ef4444':f.key==='in_stock'?'#22c55e':'#1a56db'}}/>
                <span className={styles.filter_label}>{f.label}</span>
                <span className={`${styles.filter_count} ${stockFilter===f.key?styles.filter_count_active:''}`}>{cnt}</span>
              </div>
            );
          })}
          {cats.length > 0 && <><div className={styles.filter_divider}/><div className={styles.filter_section}>By Category</div>
            {cats.map(cat => (
              <div key={cat} className={styles.filter_item} onClick={()=>setSearch(cat)}>
                <span style={{fontSize:14}}>📦</span>
                <span className={styles.filter_label}>{cat}</span>
                <span className={styles.filter_count}>{spares.filter(p=>p.category===cat).length}</span>
              </div>
            ))}
          </>}
        </div>

        <div className={styles.list_col}>
          {loading ? <div className={styles.loading}>Loading…</div>
          : filtered.length === 0 ? (
            <div className={styles.empty}><div className={styles.empty_icon}>📦</div><div className={styles.empty_title}>No parts found</div><div className={styles.empty_sub}>Add spare parts to track inventory</div></div>
          ) : filtered.map(part => {
            const isLow = part.stock_qty <= part.reorder_level;
            const isSel = selected?.id === part.id;
            return (
              <div key={part.id} className={`${styles.card} ${isSel?styles.card_active:''}`} onClick={()=>setSelected(part)}>
                <div className={styles.card_stripe} style={{background:isLow?'#ef4444':'#22c55e'}}/>
                <div className={styles.card_body}>
                  <div className={styles.card_top}>
                    <span className={styles.card_ref}>{part.part_number}</span>
                    <span className={styles.badge} style={{background:isLow?'#fee2e2':'#dcfce7',color:isLow?'#b91c1c':'#15803d'}}>{isLow?'Low Stock':'In Stock'}</span>
                  </div>
                  <div className={styles.card_title}>{part.name}</div>
                  <div className={styles.card_meta}>{[part.category, part.location].filter(Boolean).join(' · ')}</div>
                  <div className={styles.card_footer}>
                    <span style={{fontSize:13,fontWeight:700,color:isLow?'#ef4444':'#1e2a3b'}}>{part.stock_qty} {part.unit}</span>
                    <span style={{fontSize:12,color:'#64748b'}}>Reorder at {part.reorder_level} · ₹{Number(part.unit_cost).toLocaleString('en-IN')}/unit</span>
                  </div>
                </div>
                <div className={styles.card_actions}>
                  <button className={styles.card_action_btn} title="Update Stock" onClick={e=>{e.stopPropagation();setSelected(part);setShowEdit(true);}}>📥</button>
                </div>
              </div>
            );
          })}
        </div>

        <div className={styles.detail_col}>
          <DetailPanel part={selected} onEditStock={()=>setShowEdit(true)}/>
        </div>
      </div>

      {showAdd  && <AddPartModal onClose={()=>setShowAdd(false)} onSave={()=>{setShowAdd(false);load();}}/>}
      {showEdit && selected && <EditStockModal part={selected} onClose={()=>setShowEdit(false)} onSave={()=>{setShowEdit(false);load();}}/>}
    </div>
  );
}
